import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/core/database/supabase';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { PermissionDeniedState } from './PermissionDeniedState';
import { getRequiredPermissionForRoute, baseAuthenticatedRoutes } from '@/config/router.permissions';

interface AppRouteGuardProps {
  children: React.ReactNode;
}

/**
 * AppRouteGuard - Lớp bảo vệ Router tập trung chuẩn Enterprise
 * Sử dụng Router Matrix để ánh xạ URL -> permission_key.
 * Mọi URL chưa khai báo sẽ bị Default Deny (Chặn).
 */
export function AppRouteGuard({ children }: AppRouteGuardProps) {
  const location = useLocation();
  const { user, isLoading: userLoading } = useCurrentUser();
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkAccess() {
      if (userLoading || !user?.id) return;

      setIsLoading(true);
      try {
        // 1. Kiểm tra xem route có thuộc diện cơ bản (đã đăng nhập là vào được) không
        const isBaseRoute = baseAuthenticatedRoutes.some(route => location.pathname.startsWith(route));
        if (isBaseRoute) {
          setIsAllowed(true);
          setIsLoading(false);
          return;
        }

        // 2. Lấy mã quyền yêu cầu từ Router Matrix
        const requiredPermission = getRequiredPermissionForRoute(location.pathname);
        
        // Nguyên tắc Default Deny: Nếu không khai báo quyền -> Từ chối truy cập
        if (!requiredPermission) {
          console.warn(`[RouteGuard] Default Deny: Route ${location.pathname} chưa được khai báo permission_key trong Router Matrix.`);
          setIsAllowed(false);
          setIsLoading(false);
          return;
        }

        // Gọi RPC kiểm tra quyền từ Database
        const { data, error } = await supabase.rpc('fn_has_permission', {
          p_user_id: user.id,
          p_permission_key: requiredPermission
        });

        if (error) {
          console.error('[RouteGuard] Check Permission Error:', error);
          setIsAllowed(false); 
        } else {
          setIsAllowed(Boolean(data));
        }
      } catch (err) {
        console.error('[RouteGuard] Exception:', err);
        setIsAllowed(false);
      } finally {
        setIsLoading(false);
      }
    }

    checkAccess();
  }, [location.pathname, user?.id, userLoading]);

  // Loading state cho Guard
  if (userLoading || isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-white/50 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-100 border-t-brand-600" />
          <span className="text-xs font-medium text-slate-500">Đang xác thực quyền truy cập...</span>
        </div>
      </div>
    );
  }


  // Nếu không có quyền -> Hiển thị màn hình chặn tại chỗ
  if (isAllowed === false) {
    return (
      <div className="p-8">
        <PermissionDeniedState 
          title="Trang web bị khóa" 
          message="Module này yêu cầu quyền hạn cao hơn. Nếu bạn tin rằng đây là một sai sót, vui lòng liên hệ Admin."
        />
      </div>
    );
  }

  return <>{children}</>;
}
